import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { X, CheckCircle2, AlertTriangle, AlertOctagon, Info, Loader2, CalendarClock } from "lucide-react";

/**
 * Iter 130.af · Wizard de checkpoint pre-cierre de mes.
 *
 * Aparece automáticamente entre el día 25 y fin de mes si no se ha completado
 * o descartado para ese mes. Ejecuta 5 validaciones (facturas, borradores,
 * compras incompletas, IVA del trimestre, certificaciones sin facturar).
 *
 * Persiste la finalización por mes en `localStorage`.
 */
const STATE_COLOR = {
  ok: { bg: "rgba(15,138,95,0.10)", color: "#0F8A5F", Icon: CheckCircle2, label: "OK" },
  warning: { bg: "rgba(224,138,31,0.10)", color: "#E08A1F", Icon: AlertTriangle, label: "AVISO" },
  error: { bg: "rgba(220,38,38,0.10)", color: "#DC2626", Icon: AlertOctagon, label: "ERROR" },
  info: { bg: "rgba(30,91,204,0.10)", color: "#1E5BCC", Icon: Info, label: "INFO" },
};
const VEREDICTO = {
  listo: { color: "#0F8A5F", titulo: "✅ Mes listo para cerrar", desc: "Todas las comprobaciones están en verde." },
  revisar: { color: "#E08A1F", titulo: "⚠ Revisa antes de cerrar", desc: "Hay avisos que conviene atender." },
  no_cerrar: { color: "#DC2626", titulo: "🛑 No cierres todavía", desc: "Hay errores graves a corregir." },
};

export default function CheckpointCierreMesWizard() {
  const navigate = useNavigate();
  const ahora = new Date();
  const ejercicio = ahora.getFullYear();
  const mes = ahora.getMonth() + 1;
  const dia = ahora.getDate();
  const mesStr = `${ejercicio}-${String(mes).padStart(2, "0")}`;
  const storageKey = `checkpoint-cierre-${mesStr}`;

  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Mostrar entre día 25 y fin de mes, si no se ha cerrado/descartado para ese mes
  useEffect(() => {
    if (dia < 25) return;
    let dismissed = false;
    try { dismissed = localStorage.getItem(storageKey) === "1"; } catch {/* ignore */}
    if (dismissed) return;
    // Auto-mostrar tras 2 seg para que el usuario perciba la transición
    const t = setTimeout(() => setOpen(true), 2000);
    return () => clearTimeout(t);
  }, [dia, storageKey]);

  // Cargar datos al abrir
  useEffect(() => {
    if (!open || data) return;
    setLoading(true);
    api.get(`/fiscal/sl/checkpoint-cierre/${ejercicio}/${mes}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatErr(e)))
      .finally(() => setLoading(false));
  }, [open, data, ejercicio, mes]);

  const cerrarDescartar = () => {
    try { localStorage.setItem(storageKey, "1"); } catch {/* ignore */}
    setOpen(false);
  };
  const cerrarSinDescartar = () => setOpen(false);

  // Botón flotante para reabrirlo manualmente
  const reAbrir = () => { setData(null); setOpen(true); };
  let dismissed = false;
  try { dismissed = localStorage.getItem(storageKey) === "1"; } catch {/* ignore */}
  // Si está descartado y dia >= 25, mantener pequeño FAB para reabrir
  const mostrarFAB = dia >= 25 && dismissed;

  return (
    <>
      {mostrarFAB && (
        <button
          onClick={reAbrir}
          className="fixed bottom-20 right-4 z-40 px-3 py-2 rounded-full text-xs font-bold text-white shadow-lg hover:shadow-xl transition"
          style={{ background: "linear-gradient(135deg, #6D43B8, #4F2F87)" }}
          title="Reabrir checkpoint de cierre de mes"
          data-testid="checkpoint-cierre-reopen"
        >
          <CalendarClock className="h-3.5 w-3.5 inline mr-1" /> Cierre {mesStr}
        </button>
      )}

      <Dialog open={open} onOpenChange={(v) => v ? setOpen(true) : cerrarSinDescartar()}>
        <DialogContent className="max-w-3xl" data-testid="checkpoint-cierre-wizard">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarClock className="h-5 w-5" style={{ color: "#6D43B8" }} />
              Checkpoint pre-cierre · {mesStr}
            </DialogTitle>
          </DialogHeader>

          {loading || !data ? (
            <div className="py-12 text-center text-sm text-[var(--legatum-text-muted)]">
              <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" /> Analizando el mes…
            </div>
          ) : (
            <div className="space-y-3">
              {/* Veredicto general */}
              <div className="rounded-xl p-4" style={{
                background: `${VEREDICTO[data.veredicto].color}1A`,
                border: `2px solid ${VEREDICTO[data.veredicto].color}`,
              }} data-testid="checkpoint-veredicto">
                <div className="text-sm font-extrabold" style={{ color: VEREDICTO[data.veredicto].color }}>
                  {VEREDICTO[data.veredicto].titulo}
                </div>
                <div className="text-[11px] mt-0.5 text-[var(--legatum-text-secondary)]">
                  {VEREDICTO[data.veredicto].desc} · {data.errores} error(es) · {data.warnings} aviso(s).
                </div>
              </div>

              {/* Lista de checks */}
              <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                {data.checks.map((c) => {
                  const st = STATE_COLOR[c.estado] || STATE_COLOR.info;
                  return (
                    <div key={c.id} className="rounded-lg border p-3" style={{ borderColor: `${st.color}55`, background: st.bg }} data-testid={`checkpoint-${c.id}`}>
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <div className="flex items-start gap-2 min-w-0">
                          <st.Icon className="h-4 w-4 mt-0.5 shrink-0" style={{ color: st.color }} />
                          <div className="min-w-0">
                            <div className="text-sm font-bold">{c.titulo}</div>
                            <div className="text-[11px] text-[var(--legatum-text-secondary)] mt-0.5">{c.detalle}</div>
                            {c.muestra && c.muestra.length > 0 && (
                              <ul className="mt-1 text-[10px] text-[var(--legatum-text-muted)] pl-3 list-disc">
                                {c.muestra.map((m, i) => (
                                  <li key={i}>{m.proveedor} · {m.concepto} ({m.fecha})</li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </div>
                        <span className="text-[9px] uppercase font-bold tracking-wider px-2 py-0.5 rounded shrink-0" style={{ background: st.color, color: "#fff" }}>
                          {st.label}
                        </span>
                      </div>
                      {c.accion && c.estado !== "ok" && (
                        <Button
                          variant="link"
                          size="sm"
                          className="text-[11px] p-0 h-auto mt-1"
                          style={{ color: st.color }}
                          onClick={() => {
                            if (c.id === "borradores_pendientes" || c.id === "facturas_emitidas") navigate("/facturacion");
                            else if (c.id === "compras_incompletas") navigate("/compras");
                            else if (c.id === "iva_cuadre") navigate("/fiscal-sl?tab=iva");
                            else if (c.id === "certificaciones_sin_facturar") navigate("/gerencia");
                          }}
                          data-testid={`checkpoint-action-${c.id}`}
                        >
                          {c.accion} →
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 flex-wrap">
            <Button variant="outline" onClick={cerrarSinDescartar} data-testid="checkpoint-cerrar">
              Cerrar
            </Button>
            <Button
              onClick={cerrarDescartar}
              variant="outline"
              disabled={loading}
              data-testid="checkpoint-marcar-revisado"
            >
              <CheckCircle2 className="h-3 w-3 mr-1" /> Marcar revisado (no cerrar mes)
            </Button>
            {data && data.veredicto !== "no_cerrar" && (
              <Button
                onClick={async () => {
                  if (!window.confirm(`¿Cerrar contablemente el mes ${data.mes_str}?\n\nUna vez cerrado, las facturas y compras de este mes NO podrán editarse ni emitirse, salvo que un ADMINISTRADOR lo desbloquee con justificación.\n\n¿Continuar?`)) return;
                  try {
                    const r = await api.post(`/fiscal/sl/cierre-mes/${data.ejercicio}/${data.mes}`);
                    toast.success(`🔒 Mes ${data.mes_str} cerrado · hash ${r.data.hash_sha256.slice(0, 12)}…`, { duration: 8000 });
                    cerrarDescartar();
                  } catch (e) { toast.error(formatErr(e)); }
                }}
                className="text-white font-bold"
                style={{ background: "linear-gradient(135deg, #DC2626, #991B1B)" }}
                disabled={loading}
                data-testid="checkpoint-cerrar-mes"
              >
                🔒 Cerrar mes contablemente
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
